import { isObject } from '@scalar/helpers/object/is-object';
import { getResolvedRef, mergeSiblingReferences } from '@scalar/workspace-store/helpers/get-resolved-ref';
import { inlineCode, item, list, paragraph, strong, text } from './markdown-nodes.js';
/** Boolean targets still combine with adjacent schema keywords. */
const resolveMarkdownSchema = (input) => {
    const target = getResolvedRef(input);
    const merged = getResolvedRef(input, mergeSiblingReferences);
    if (typeof target !== 'boolean')
        return merged;
    if (!isObject(merged) ||
        !Object.keys(merged).some((key) => !['$ref', '$ref-value', '$global', '$status'].includes(key)))
        return target;
    if (target)
        return merged;
    // A false target remains impossible, even when siblings describe a type or annotations.
    return { ...merged, allOf: [false, ...(Array.isArray(merged.allOf) ? merged.allOf : [])] };
};
/** Keep merged reference siblings and sorted properties stable throughout an export. */
export const createSchemaRenderer = () => {
    const views = new WeakMap();
    const view = (input) => {
        const cached = typeof input === 'object' ? views.get(input) : undefined;
        if (cached)
            return cached;
        // Keep the linked document's nested identities and boolean schemas. Coercing a
        // second time here would discard boolean children and copy recursive targets.
        const schema = resolveMarkdownSchema(input);
        const value = (typeof schema === 'object' ? schema : {});
        const required = new Set(value.required ?? []);
        const properties = Object.entries(value.properties ?? {})
            .filter(([, child]) => typeof child === 'boolean' || (child !== null && typeof child === 'object'))
            .sort(([a], [b]) => Number(required.has(b)) - Number(required.has(a)) || a.localeCompare(b));
        const result = {
            ...value,
            type: typeof schema === 'boolean' ? (schema ? 'any' : 'never') : value.type,
            schema: schema,
            required,
            properties,
        };
        if (typeof input === 'object')
            views.set(input, result);
        return result;
    };
    const details = (value, property = false, hideDescription = false, showType = true) => {
        if (typeof value.schema === 'boolean')
            return [text(value.schema ? 'any (true schema)' : 'never (false schema)')];
        const type = Array.isArray(value.type) ? value.type.join(' | ') : value.type;
        const nodes = showType && (type || property) ? [inlineCode(type || 'object')] : [];
        const add = (label, entry) => {
            if (entry !== undefined)
                nodes.push(text(`${nodes.length ? ', ' : ''}${label}: `), inlineCode(entry));
        };
        add('format', value.format);
        if (value.enum)
            add('possible values', value.enum.map((entry) => JSON.stringify(entry)).join(', '));
        if (value.const !== undefined)
            add('const', JSON.stringify(value.const));
        if (value.default !== undefined)
            add('default', JSON.stringify(value.default));
        for (const key of [
            'minimum',
            'maximum',
            'exclusiveMinimum',
            'exclusiveMaximum',
            'multipleOf',
            'minLength',
            'maxLength',
            'pattern',
            'minProperties',
            'maxProperties',
        ])
            add(key, value[key]);
        for (const key of ['readOnly', 'writeOnly']) {
            if (value[key])
                nodes.push(text(`${nodes.length ? ', ' : ''}${key}`));
        }
        if (!hideDescription && value.description)
            nodes.push(text(`${nodes.length ? ' — ' : ''}${value.description}`));
        return nodes;
    };
    const render = (input, depth = 0, ancestors = [], options = {}) => {
        // Follow the original target: merging reference siblings creates fresh objects.
        const identity = getResolvedRef(input) ?? input;
        if (typeof identity === 'object' && ancestors.includes(identity)) {
            return [paragraph({ type: 'emphasis', children: [text('[Circular Reference]')] })];
        }
        if (depth >= 64)
            return [paragraph(text('[Maximum schema depth reached]'))];
        const value = view(input);
        if (typeof value.schema === 'boolean')
            return options.hideDetails ? [] : [paragraph(...details(value))];
        const childAncestors = [...ancestors, identity];
        const nodes = [];
        for (const [key, label] of [
            ['allOf', 'All of:'],
            ['anyOf', 'Any of:'],
            ['oneOf', 'One of:'],
        ]) {
            if (value[key]?.length)
                nodes.push(paragraph(strong(text(label))), ...value[key].flatMap((child) => render(child, depth + 1, childAncestors)));
        }
        if (value.not !== undefined)
            nodes.push(paragraph(strong(text('Not:'))), ...render(value.not, depth + 1, childAncestors));
        const array = value.type === 'array' || value.items !== undefined;
        if (!options.hideDetails) {
            // Child sections imply a single container type, but never its nullable alternatives.
            const impliedType = (value.type === 'object' && value.properties.length > 0) ||
                (value.type === 'array' && value.items !== undefined);
            const annotations = details(value, false, options.hideDescription, !impliedType);
            if (annotations.length)
                nodes.push(paragraph(...annotations));
        }
        if (value.properties.length) {
            const properties = value.properties.map(([name, schema]) => {
                const child = view(schema);
                const label = [inlineCode(name)];
                if (value.required.has(name))
                    label.push(text(' (required)'));
                const blocks = [paragraph(strong(...label)), paragraph(...details(child, true))];
                blocks.push(...render(schema, depth + 1, childAncestors, { hideDetails: true, property: true }));
                return item(...blocks);
            });
            nodes.push(list(properties));
        }
        if (array && value.items !== undefined) {
            nodes.push(paragraph(strong(text(options.property ? 'Items:' : 'Array of:'))), ...render(value.items, depth + 1, childAncestors));
        }
        const constraints = [];
        if (value.minItems !== undefined)
            constraints.push(item(paragraph(text('Min items: '), inlineCode(value.minItems))));
        if (value.maxItems !== undefined)
            constraints.push(item(paragraph(text('Max items: '), inlineCode(value.maxItems))));
        if (value.uniqueItems !== undefined)
            constraints.push(item(paragraph(text('Unique items: '), inlineCode(value.uniqueItems))));
        if (constraints.length)
            nodes.push(list(constraints));
        if (value.additionalProperties !== undefined)
            nodes.push(paragraph(strong(text('Additional properties:'))), ...render(value.additionalProperties, depth + 1, childAncestors));
        if (value.discriminator) {
            nodes.push(paragraph(strong(text('Discriminator:')), text(' '), inlineCode(value.discriminator.propertyName)));
            const mappings = Object.entries(value.discriminator.mapping ?? {});
            if (mappings.length)
                nodes.push(list(mappings.map(([name, target]) => item(paragraph(inlineCode(name), text(': '), inlineCode(target))))));
        }
        return nodes;
    };
    return { view, render };
};
